/**
 * Invoice detail action ribbon — `prototype/sales/invoice-detail.html` ▸
 * `.ribbon`. Renders nothing when the status doesn't warrant a banner.
 *
 * Visibility rules (per spec §4 step 4):
 *   - `issued` + due ≤ 14 days       → amber "Awaiting payment"
 *   - `partially_paid`               → indigo "Part payment received"
 *   - `overdue`                      → rose   "Payment overdue"
 *   - everything else                → null
 */
import { Clock, CreditCard, TriangleAlert } from "lucide-react";
import { formatDate, paiseToInrShort } from "../../../lib/format.ts";
import type { InvoiceStatus } from "./invoice-detail-header.tsx";

/** Status and balance inputs used to decide whether a ribbon is needed. */
export interface InvoiceDetailRibbonProps {
  status: InvoiceStatus;
  dueOn: string;
  totalPaise: string;
  receivedPaise: bigint;
  /** Sum of credit-note totals raised against this invoice. */
  creditedPaise: bigint;
}

/** Render the conditional invoice action ribbon above the document. */
export function InvoiceDetailRibbon({
  status,
  dueOn,
  totalPaise,
  receivedPaise,
  creditedPaise,
}: InvoiceDetailRibbonProps) {
  const total = BigInt(totalPaise);
  const outstanding = total - receivedPaise - creditedPaise;
  const isFullyReversed = creditedPaise >= total;
  const hasPartialCredit = creditedPaise > 0n && !isFullyReversed;

  // When the invoice is fully reversed by credit notes we DON'T render
  // a ribbon here — the InvoiceDoc itself draws a red stamp across its
  // header so the cancellation is visible on the document the operator
  // is reading, not as a separate banner that competes for attention.
  if (isFullyReversed) {
    return null;
  }

  if (status === "overdue") {
    return (
      <section className="ribbon rose" role="status">
        <span className="ic" aria-hidden>
          <TriangleAlert size={14} />
        </span>
        <div className="body">
          <strong>Payment overdue</strong> — due {formatDate(dueOn)}.
          {hasPartialCredit ? (
            <>
              {" "}
              <span className="num">{paiseToInrShort(creditedPaise)}</span> credited; still
              <span className="num"> {paiseToInrShort(outstanding)}</span> due.
            </>
          ) : null}{" "}
          Send a reminder; keep the message factual and link back to this invoice.
        </div>
      </section>
    );
  }

  if (status === "partially_paid") {
    return (
      <section className="ribbon indigo" role="status">
        <span className="ic" aria-hidden>
          <CreditCard size={14} />
        </span>
        <div className="body">
          <strong>Part payment received</strong> ·{" "}
          <span className="num">{paiseToInrShort(outstanding)}</span> outstanding
          {hasPartialCredit ? (
            <>
              {" "}
              (after <span className="num">{paiseToInrShort(creditedPaise)}</span> credit note)
            </>
          ) : null}
          . Record the next receipt so the audit trail stays complete.
        </div>
      </section>
    );
  }

  if (status === "issued") {
    const days = Math.ceil((new Date(dueOn).getTime() - Date.now()) / 86_400_000);
    if (days <= 14) {
      return (
        <section className="ribbon amber" role="status">
          <span className="ic" aria-hidden>
            <Clock size={14} />
          </span>
          <div className="body">
            <strong>Awaiting payment</strong> · sent on WhatsApp. Due{" "}
            <span className="num">{formatDate(dueOn)}</span> ({days}d).
            {hasPartialCredit ? (
              <>
                {" "}
                <span className="num">{paiseToInrShort(creditedPaise)}</span> credited; net{" "}
                <span className="num">{paiseToInrShort(outstanding)}</span> still due.
              </>
            ) : null}{" "}
            Follow up before the due date if the customer usually pays late.
          </div>
        </section>
      );
    }
  }

  return null;
}
